import { useState } from "react";
import { AlertCircle, ArrowRight, Check, Copy, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { t } from "@/lib/i18n";
import {
  Hint,
  SideCard,
  SuccessBox,
  Steps,
  monoInputCls,
  type PanelProps,
} from "./channel-wizard-kit";

/* Telegram: токен бота от @BotFather, проверка соединения и подключение. */

const DEMO_TOKEN = "7724190345:AAH8f2kLm9pQrStUvWxYzHUB";
const TOKEN_RE = /^\d{6,12}:[A-Za-z0-9_-]{20,}$/;

export default function TelegramPanel({ onDone }: PanelProps) {
  const [token, setToken] = useState("");
  const [check, setCheck] = useState<"idle" | "checking" | "ok" | "error">("idle");
  const [copied, setCopied] = useState(false);

  const verifyToken = () => {
    setCheck("checking");
    setTimeout(() => setCheck(TOKEN_RE.test(token.trim()) ? "ok" : "error"), 800);
  };

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(DEMO_TOKEN);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const connect = () => {
    onDone(t("Бот @hub_support_bot"), [
      { label: t("Тип"), value: t("Telegram · бот") },
      { label: t("Бот"), value: "Hub Support Bot" },
      { label: t("Никнейм"), value: "@hub_support_bot" },
      { label: t("Команды"), value: "/start, /help, /agent" },
      { label: t("Токен"), value: t("сохранён в защищённом виде") },
    ]);
  };

  return (
    <div className="grid flex-1 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="min-w-0 p-4 md:p-5">
        <Steps
          active={check === "ok" ? 3 : token.trim().length > 0 ? 2 : 1}
          items={[
            t("Откройте @BotFather и создайте бота"),
            t("Скопируйте токен вида 1234567890:AA…"),
            t("Вставьте токен и проверьте соединение"),
          ]}
        />

        <div className="mt-4">
          <label
            htmlFor="wizard-token"
            className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
          >
            {t("Токен бота")}
          </label>
          <div className="mt-1.5 flex flex-col gap-2 sm:flex-row">
            <input
              id="wizard-token"
              value={token}
              onChange={(e) => {
                setToken(e.target.value);
                setCheck("idle");
              }}
              placeholder="7724190345:AAH8f2kLm9pQrStUvWxYz"
              autoComplete="off"
              spellCheck={false}
              className={monoInputCls}
            />
            <button
              type="button"
              onClick={verifyToken}
              disabled={token.trim().length === 0 || check === "checking"}
              className="inline-flex h-10 flex-none cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {check === "checking" ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
              {check === "checking" ? t("Проверяем…") : t("Проверить токен")}
            </button>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setToken(DEMO_TOKEN);
                setCheck("idle");
              }}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold text-primary transition hover:brightness-110"
            >
              <Sparkles className="size-3" aria-hidden />
              {t("Вставить демо-токен")}
            </button>
            <span className="text-[11px] text-muted-foreground">·</span>
            <button
              type="button"
              onClick={copyToken}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[11px] font-medium text-muted-foreground transition hover:text-foreground"
            >
              {copied ? (
                <Check className="size-3 text-primary" aria-hidden />
              ) : (
                <Copy className="size-3" aria-hidden />
              )}
              {copied ? t("Скопировано") : t("Скопировать пример")}
            </button>
          </div>

          {check === "error" ? (
            <p className="mt-2.5 flex items-start gap-1.5 rounded-btn border border-red-400/40 bg-red-500/10 px-2.5 py-2 text-[11.5px] leading-relaxed text-red-600">
              <AlertCircle className="mt-0.5 size-3.5 flex-none" aria-hidden />
              {t("Токен не подходит. Нужен вида «1234567890:AAH8f2kLm9pQrStUvWxYz» — без пробелов и лишних символов.")}
            </p>
          ) : null}

          {check === "ok" ? (
            <SuccessBox title={t("Токен рабочий — бот отвечает")}>
              <div className="flex flex-col">
                {[
                  [t("Бот"), "Hub Support Bot"],
                  [t("Никнейм"), "@hub_support_bot"],
                  ["ID", "7724190345"],
                  [t("Вебхук"), t("установлен")],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="flex items-center justify-between gap-3 border-b border-emerald-600/20 py-1.5 last:border-0"
                  >
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                      {label}
                    </span>
                    <span className="text-[12.5px] font-semibold">{value}</span>
                  </div>
                ))}
              </div>
            </SuccessBox>
          ) : null}

          <button
            type="button"
            onClick={connect}
            disabled={check !== "ok"}
            className="mt-3 inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("Подключить бота")}
            <ArrowRight className="size-3.5" aria-hidden />
          </button>
        </div>

        <Hint icon={<ShieldCheck className="size-3.5" />}>
          {t("Токен видит только администратор. Продавцы получают диалоги, а не доступы.")}
        </Hint>
      </div>

      <div className="flex min-w-0 flex-col gap-3 border-t border-border bg-background p-4 lg:border-s lg:border-t-0 md:p-5">
        <SideCard title={t("Превью бота")}>
          <div className="mt-2.5 flex items-center gap-2.5">
            <span
              className="grid size-10 flex-none place-items-center rounded-full bg-primary/10 text-lg leading-none text-primary"
              aria-hidden
            >
              <i className="ri-telegram-line" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-[12.5px] font-semibold">Hub Support Bot</p>
              <p className="truncate text-[11px] text-muted-foreground">
                {check === "ok" ? "@hub_support_bot" : t("ждём проверку токена")}
              </p>
            </div>
          </div>
          <div className="mt-3 rounded-btn bg-muted/70 p-2.5">
            <p className="text-[11.5px] leading-relaxed text-foreground">
              {t("Здравствуйте! Пишите — ответим в течение пары минут.")}
            </p>
            <p className="mt-1 text-[10px] text-muted-foreground">{t("так увидят бота клиенты")}</p>
          </div>
        </SideCard>

        <SideCard title={t("Что умеет после подключения")}>
          <ul className="mt-2 flex flex-col gap-1.5">
            {[
              t("Диалоги падают во «Входящие»"),
              t("Имя и ник клиента подтягиваются"),
              t("AI-агент может отвечать первым"),
            ].map((item) => (
              <li
                key={item}
                className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground"
              >
                <Check className="mt-0.5 size-3 flex-none text-primary" strokeWidth={3} aria-hidden />
                {item}
              </li>
            ))}
          </ul>
        </SideCard>
      </div>
    </div>
  );
}